// Password reset (UC-IDENTITY-007): the request answers the same whether or not the address is known; the completion page
// takes the single-use token from the link.
import { PASSWORD_MIN_LENGTH } from "@visionx/contracts";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { Alert, Field, TextInput } from "../ui/bits";
import { Mark } from "../ui/icons";

function Bare({ title, help, children }: { title: string; help?: string | undefined; children: React.ReactNode }) {
  return (
    <section className="screen" id="s-signin" data-bare="true">
      <div className="content">
        <div className="wrap">
          <div className="cards">
            <div className="stack card">
              <div className="panel">
                <div className="panel-body" style={{ padding: 24, gap: 16 }}>
                  <div className="logo"><Mark className="vxmark" /><span>Vision X</span></div>
                  <div className="stack gap-4"><h1>{title}</h1>{help && <div className="help">{help}</div>}</div>
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try { await auth.passwordReset(email); setSent(true); }
    catch (err) { setError(isApiError(err) && err.code === "RATE_LIMITED" ? "Too many reset requests. Try again later." : describeError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Bare title="Reset your password" help="Enter your practice email address. If it belongs to an active user, a link that works for 30 minutes will be sent.">
      {sent ? (
        <>
          <Alert tone="ok"><b>Check your email.</b> If that address belongs to an active user, a reset link is on its way. It works once, for 30 minutes.</Alert>
          <div className="centre"><Link className="small" to="/signin">Back to sign in</Link></div>
        </>
      ) : (
        <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submit(e)}>
          {error && <Alert tone="crit">{error}</Alert>}
          <Field label="Email" htmlFor="reset-email"><TextInput id="reset-email" type="email" autoComplete="username" required autoFocus value={email} disabled={busy} onChange={(e) => setEmail(e.target.value)} /></Field>
          <button className="btn primary full" type="submit" disabled={busy}>{busy ? "Sending…" : "Send reset link"}</button>
          <div className="centre"><Link className="small" to="/signin">Back to sign in</Link></div>
        </form>
      )}
    </Bare>
  );
}

export function PasswordResetComplete() {
  const { token = "" } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN_LENGTH) { setError(`Use at least ${PASSWORD_MIN_LENGTH} characters.`); return; }
    if (password !== confirm) { setError("The two passwords do not match."); return; }
    setBusy(true);
    try { await auth.passwordResetComplete(token, password); setDone(true); }
    catch (err) {
      if (isApiError(err) && err.code === "NOT_FOUND") setExpired(true);
      else setError(isApiError(err) && err.code === "VALIDATION_FAILED" ? (err.field("newPassword")?.message ?? describeError(err)) : describeError(err));
    } finally { setBusy(false); }
  };
  if (expired) {
    return (
      <Bare title="This link has expired">
        <Alert tone="warn"><b>Reset links work once, for 30 minutes.</b> Request a new one to continue.</Alert>
        <Link className="btn primary full" to="/password-reset">Request a new link</Link>
      </Bare>
    );
  }
  if (done) {
    return (
      <Bare title="Password changed">
        <Alert tone="ok"><b>Your password is set.</b> Every other device has been signed out. Sign in with the new password.</Alert>
        <Link className="btn primary full" to="/signin">Sign in</Link>
      </Bare>
    );
  }
  return (
    <Bare title="Choose a new password" help={`At least ${PASSWORD_MIN_LENGTH} characters. Every other device will be signed out.`}>
      <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submit(e)}>
        {error && <Alert tone="crit">{error}</Alert>}
        <Field label="New password" htmlFor="reset-password"><TextInput id="reset-password" type="password" autoComplete="new-password" required autoFocus minLength={PASSWORD_MIN_LENGTH} value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} /></Field>
        <Field label="Repeat the new password" htmlFor="reset-confirm"><TextInput id="reset-confirm" type="password" autoComplete="new-password" required value={confirm} disabled={busy} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <button className="btn primary full" type="submit" disabled={busy}>{busy ? "Saving…" : "Set password"}</button>
      </form>
    </Bare>
  );
}
